import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../services/api";
import { toast } from "react-hot-toast";
import { authentication as teamsAuthentication } from "@microsoft/teams-js";


// KEEP THE REST OF YOUR IMPORTS AND THE COMPONENT STRUCTURE THE SAME
const Login = ({ onLogin, isTeamsInitialized }) => { // Added isTeamsInitialized prop
  const location = useLocation();
  const navigate = useNavigate();

  // NEW state to manage the two-step login flow
  const [loginStep, setLoginStep] = useState('identifier'); // 'identifier' -> 'password'

  // This state now manages the sub-flow *within* the 'password' step
  const [loginFlowState, setLoginFlowState] = useState("password"); // 'password' or 'otp_request' or 'otp_verify'

  const [isNewUserFlow, setIsNewUserFlow] = useState(false); // ### ADDED: State to track if it's a new user OTP flow

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
    otp: "",
    emailForOtpDisplay: "",
    actualEmailForVerification: "",
  });
  const [otpJustSent, setOtpJustSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);

  const oauthPopupRef = useRef(null);
  const popupPollerRef = useRef(null);

  // --- START: Existing useEffect for handling URL error parameter ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const errorParam = queryParams.get("error");

    if (errorParam) {
      const decodedError = decodeURIComponent(errorParam);
      toast.error(decodedError);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [location, navigate]);
  // --- END: Existing useEffect ---

  // --- START: Existing Callback for handling OAuth messages (for web flow) ---
  const handleOauthMessage = useCallback((event) => {
    console.log("Web OAuth Event:", event);
    let expectedOrigin = "";
    try {
      expectedOrigin = new URL(API_URL).origin;
    } catch (e) {
      console.error("Invalid API_URL for origin check:", API_URL);
      expectedOrigin = window.location.origin;
    }

    // Basic origin check (can be stricter)
    if (event.origin !== expectedOrigin && new URL(window.location.href).origin !== new URL(event.origin).origin) {
      // The second check is for when API_URL is relative and backend is same-origin
      // and postMessage comes from that same origin (e.g. /api/auth/oauth2callback)
      console.warn("Message received from unexpected origin:", event.origin, "Expected:", expectedOrigin, "or", window.location.origin);
      // return; // Be cautious with this return in case of complex but valid proxy/redirect setups
    }

    if (event.data && typeof event.data === 'object') {
      const { success, token, user_id, project_token, error, provider } = event.data;
      if (typeof success === "undefined" && !token && !error) { // More specific check
        return;
      }
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
      if (popupPollerRef.current) {
        clearInterval(popupPollerRef.current);
        popupPollerRef.current = null;
      }
      oauthPopupRef.current = null;
      setIsLoading(false);

      if (success) {
        toast.success(`Logged in successfully with ${provider || 'OAuth provider'}!`);
        onLogin(token, user_id, project_token); // Assuming onLogin expects (token, userId)
      } else {
        toast.error(error || "OAuth login failed. Please try again.");
      }
    }
  }, [onLogin, API_URL]);
  // --- END: Existing Callback for handling OAuth messages ---

  // --- START: Existing useEffect for setting up message listener (for web flow) ---
  useEffect(() => {
    // Only add this listener if not in Teams context or as a fallback
    // For this setup, we'll have initiateOAuthLogin decide based on context
    // So, this listener is primarily for the non-Teams web flow.
    window.addEventListener("message", handleOauthMessage);
    return () => {
      window.removeEventListener("message", handleOauthMessage);
      if (popupPollerRef.current) {
        clearInterval(popupPollerRef.current);
      }
      if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
        oauthPopupRef.current.close();
      }
    };
  }, [handleOauthMessage]);
  // --- END: Existing useEffect ---

  // --- START: Existing openOAuthPopup (for web flow) ---
  const openOAuthPopup = (authUrl) => {
    const width = 600, height = 700;
    const left = (window.screen.width / 2) - (width / 2);
    const top = (window.screen.height / 2) - (height / 2);

    if (oauthPopupRef.current && !oauthPopupRef.current.closed) {
      oauthPopupRef.current.close();
    }
    if (popupPollerRef.current) {
      clearInterval(popupPollerRef.current);
    }

    oauthPopupRef.current = window.open(
      authUrl, "oauthWindow",
      `toolbar=no,location=no,directories=no,status=no,menubar=no,
       scrollbars=yes,resizable=yes,copyhistory=no,width=${width},
       height=${height},top=${top},left=${left}`
    );

    if (oauthPopupRef.current) {
      setIsLoading(true);
      popupPollerRef.current = setInterval(() => {
        if (oauthPopupRef.current && oauthPopupRef.current.closed) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
          if (isLoading) {
            toast.error("Login process cancelled or popup closed unexpectedly.");
            setIsLoading(false);
          }
          oauthPopupRef.current = null;
        } else if (!oauthPopupRef.current) {
          clearInterval(popupPollerRef.current);
          popupPollerRef.current = null;
        }
      }, 500);
    } else {
      toast.error("Failed to open login window. Please check your popup blocker settings.");
      setIsLoading(false);
    }
  };
  // --- END: Existing openOAuthPopup ---

  // --- START: REVISED initiateWebOAuthLogin (WEB FLOW ONLY) ---
  const initiateWebOAuthLogin = (providerBackendEndpointName) => {
    // 1. Open the popup window IMMEDIATELY to our new intermediate page.
    // This is a direct result of the user's click, so it won't be blocked.
    setIsLoading(true);

    try {
      // 2. Construct the URL for our intermediate page, passing the necessary
      // info (provider endpoint and API URL) as query parameters.
      const authStartPageUrl = `${window.location.origin}/web-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      console.log("Web Auth: Opening intermediate auth page:", authStartPageUrl);

      // 3. Use the existing openOAuthPopup function. It already handles
      // popup window references, polling for closure, and other edge cases.
      openOAuthPopup(authStartPageUrl);

    } catch (err) {
      console.error(`Error while initiating ${providerBackendEndpointName} (Web) login:`, err);
      toast.error(err.message || `Could not initiate Sign-In with ${providerBackendEndpointName}.`);
      setIsLoading(false);
    }
  };

  // --- START: NEW initiateTeamsOAuthLogin (TEAMS FLOW ONLY) ---
  const initiateTeamsOAuthLogin = async (providerBackendEndpointName, providerShortName) => {
    if (!isTeamsInitialized) {
      toast.error("Teams environment not ready. Please try again.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const finalSuccessRedirectInApp = window.location.origin + "/projects";
      const backendCallbackUri = `${API_URL}/oauth2callback`; // Ensure API_URL is defined
      const teamsStateObject = {
        original_frontend_url: finalSuccessRedirectInApp,
        app_name: providerShortName,
        isTeamsAuth: true,
      };
      const teamsCustomState = btoa(JSON.stringify(teamsStateObject));
      const authStartPageUrl = `${window.location.origin}/teams-auth-start.html` +
        `?provider_backend_endpoint_name=${providerBackendEndpointName}` +
        `&teams_custom_state=${encodeURIComponent(teamsCustomState)}` +
        `&backend_callback_uri=${encodeURIComponent(backendCallbackUri)}` +
        `&api_uri=${encodeURIComponent(API_URL)}`;

      console.log("Teams Auth: Starting with authStartPageUrl:", authStartPageUrl);

      // Use the Promise-based version of authenticate
      const result = await teamsAuthentication.authenticate({
        url: authStartPageUrl,
        width: 600,
        height: 700,
        isExternal: false, // Recommended for most tab scenarios
      });

      // Success: 'result' contains the string passed to notifySuccess()
      setIsLoading(false);
      if (result) {
        try {
          const authData = JSON.parse(result); // Expecting { token, user_id, provider }
          toast.success(`Logged in successfully with ${authData.provider || 'OAuth provider'} via Teams!`);
          onLogin(authData.token, authData.user_id, authData.project_token);
        } catch (e) {
          toast.error("Failed to process login data from Teams.");
          console.error("Error parsing auth result from Teams:", e, "Result was:", result);
        }
      } else {
        // This case should be less common with the promise, as an empty result might still be a "success"
        // but an error during the flow would throw and be caught by the catch block.
        toast.error("OAuth login via Teams completed, but no result data was returned.");
      }

    } catch (error) { // This will catch errors from authenticate() or other issues
      setIsLoading(false);
      // The 'error' here could be a string (reason from notifyFailure) or an Error object
      const errorMessage = (error instanceof Error) ? error.message : String(error);
      toast.error(`Teams OAuth failed: ${errorMessage || "Unknown error"}`);
      console.error(`Error while initiating ${providerBackendEndpointName} (Teams) login:`, error);
    }
  };

  // --- END: NEW initiateTeamsOAuthLogin ---
  // --- CORRECTED: OAuth button handlers ---
  const handleGoogleLogin = () => {
    if (isTeamsInitialized) { // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin("redirect-url/google", "google");
    } else {
      initiateWebOAuthLogin("redirect-url/google");
    }
  };

  const handleMicrosoftLogin = () => {
    if (isTeamsInitialized) { // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin("redirect-url/outlook", "outlook");
    } else {
      initiateWebOAuthLogin("redirect-url/outlook");
    }
  };

  const handleLinkedInLogin = () => {
    if (isTeamsInitialized) { // Only rely on isTeamsInitialized
      initiateTeamsOAuthLogin("redirect-url/linkedin", "linkedin");
    } else {
      initiateWebOAuthLogin("redirect-url/linkedin");
    }
  };
  // --- END: Modified OAuth button handlers ---

  // KEEP ALL YOUR OTHER HANDLERS (handleChange, password sign-in, OTP, etc.) AND JSX THE SAME
  // ... (formData, handleChange, startResendOtpTimer, useEffect for timer, handlePasswordSignIn, handleRequestLoginOtp, handleOtpSignIn, switchToOtpFlow, switchToPasswordFlow)
  // ... (currentFormSubmitHandler, mainButtonText logic)
  // ... (return statement with JSX)

  // Ensure the prop `isTeamsInitialized` is passed from App.js
  // Example of how it might be logged (optional):
  useEffect(() => {
    console.log("LoginDemo: isTeamsInitialized prop:", isTeamsInitialized);
    if (isTeamsInitialized && (!window.microsoftTeams)) {
      console.warn("LoginDemo: isTeamsInitialized is true, but Teams SDK seems unavailable on window.microsoftTeams.app");
    }
  }, [isTeamsInitialized]);


  // ... The rest of your component (handleChange, password/OTP logic, JSX) ...
  // The JSX for buttons (handleGoogleLogin, etc.) remains the same.

  // Your existing password and OTP logic:
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const goBackToIdentifierStep = () => {
    setLoginStep('identifier');
    setLoginFlowState('password'); // Reset sub-flow to default
    setFormData(prev => ({ ...prev, password: '', otp: '' }));
  };

  // --- NEW: Step 1 Handler ---
  const handleIdentifierCheck = async (e) => {
    e.preventDefault();
    const { identifier } = formData;
    if (!identifier) {
      toast.error("Please provide your Email.");
      return;
    }
    setIsLoading(true);
    try {
      // This endpoint needs to be created on your backend as discussed.
      const response = await fetch(`${API_URL}/api/auth/check-identifier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Error checking your details.");
      }

      if (data.user_exists) {
        // User exists, so proceed to the password step
        setLoginStep('password');
      } else {
        if (data.is_email) {
          // User does not exist, but it's an email -> redirect to signup
          navigate('/sign-up', {
            state: {
              email: data.email,
              suggested_username: data.suggested_username
            }
          });
        } else {
          // User does not exist and it's not an email -> show error
          toast.error("Invalid Email.");
        }
      }
    } catch (err) {
      toast.error(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  const startResendOtpTimer = () => {
    setResendDisabled(true);
    setOtpTimer(60);
  };

  useEffect(() => {
    let intervalId = null;
    if (resendDisabled && otpTimer > 0) {
      intervalId = setInterval(() => {
        setOtpTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(intervalId);
            setResendDisabled(false);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [resendDisabled, otpTimer]);


  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    const { identifier, password } = formData;
    if (!identifier || !password) {
      toast.error("Please fill in your Email and Password.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, { // Assuming /api/auth/login is correct
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.detail || data.message || "Login failed.";
        throw new Error(errorMsg); // Throw an error object
      }
      toast.success(data.message || "Logged in Successfully!");
      // Assuming your onLogin expects (token, user_id) but backend sends {token, user_id, message}
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      console.error("Password login error:", err);
      toast.error(err.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestLoginOtp = async (e) => {
    if (e) e.preventDefault();
    const { identifier } = formData;
    if (!identifier) {
      toast.error("Please enter your Email to receive a code.");
      return;
    }
    setIsLoading(true);
    setOtpJustSent(false);
    try {
      const response = await fetch(`${API_URL}/api/auth/login/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.detail || data.message || "Failed to send one-time code.";
        throw new Error(errorMsg);
      }
      //  ### ADDED: This is the key logic to determine the next step
      // The backend sends `user_exists: true` or `user_exists: false`.
      // We set our new state based on this. `isNewUserFlow` will be true if user_exists is false.
      setIsNewUserFlow(data.new_user || false);

      toast.success(data.message || "One-time code sent to your registered email.");
      setFormData(prev => ({
        ...prev,
        emailForOtpDisplay: data.email_sent_to,
        actualEmailForVerification: data.actual_email_for_verification,
        otp: ""
      }));
      setLoginFlowState("otp_verify");
      setOtpJustSent(true);
      startResendOtpTimer();
    } catch (err) {
      console.error("Send OTP error:", err);
      toast.error(err.message || "Could not send one-time code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSignIn = async (e) => {
    e.preventDefault();
    const { actualEmailForVerification, otp } = formData;
    if (!otp) {
      toast.error("Please enter the one-time code.");
      return;
    }
    if (!actualEmailForVerification) {
      toast.error("Verification email not found. Please try requesting a code again.");
      return;
    }
    setIsLoading(true);

    // ### ADDED: Conditional logic for endpoint and payload
    const endpoint = isNewUserFlow
      ? `${API_URL}/api/auth/login/verify-and-create-user` // Endpoint for new users
      : `${API_URL}/api/auth/login/verify-otp`;      // Endpoint for existing users
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_for_verification: actualEmailForVerification, otp }),
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) {
        const errorMsg = data.detail || data.message || "Sign in with code failed.";
        throw new Error(errorMsg);
      }
      toast.success(data.message || "Logged in Successfully with code!");
      onLogin(data.token, data.user_id, data.project_token);
    } catch (err) {
      console.error("OTP login error:", err);
      toast.error(err.message || "Sign in with code failed. Please check the code or try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const switchToOtpFlow = () => {
    setLoginFlowState("otp_request");
    // ### ADDED: Reset the flow type when switching
    setIsNewUserFlow(false);
    setFormData(prev => ({
      ...prev,
      password: "",
      otp: ""
    }));
  };

  const switchToPasswordFlow = () => {
    setLoginFlowState("password");
    // ### ADDED: Reset the flow type when switching back
    setIsNewUserFlow(false);
    setOtpJustSent(false);
    setFormData(prev => ({
      ...prev,
      otp: "",
    }));
  };

  let currentFormSubmitHandler;
  let mainButtonText;

  if (loginStep === 'identifier') {
    currentFormSubmitHandler = handleIdentifierCheck;
    mainButtonText = "Continue";
  } else { // loginStep === 'password'
    if (loginFlowState === 'password') {
      currentFormSubmitHandler = handlePasswordSignIn;
      mainButtonText = "Sign in";
    } else if (loginFlowState === 'otp_request') {
      currentFormSubmitHandler = handleRequestLoginOtp;
      mainButtonText = "Send Code";
    } else { // otp_verify
      currentFormSubmitHandler = handleOtpSignIn;
      mainButtonText = "Sign In with Code";
    }
  }

  const theme = "light"; // Later replace with context or store
  const isDark = theme === "dark";

  return (
    <div
      className={`
      h-screen w-screen flex overflow-hidden flex-col md:flex-row relative
      ${isDark ? "bg-black" : "bg-radial-gradient"}
    `}
    >
      {/* DARK BG IMAGE */}
      {isDark && (
        <img
          src="../assets/auth-bg.svg"
          className="hidden lg:block absolute inset-0 h-full w-full object-cover z-0"
          alt="Background"
        />
      )}

      {/* LEFT LOGIN */}
      <div className="flex flex-col items-center justify-center w-full lg:w-1/2 p-5 z-10 relative overflow-y-auto">
        <div className="max-w-md w-full">

          {/* LOGO */}
          <div className="flex items-center mb-5">
            <img src="../assets/super_agi_logo.png" className="w-16 h-16" />
            <h1 className={`${isDark ? "text-white" : "text-black"} font-poppins text-lg font-bold ml-2`}>
              SuperManager AGI
            </h1>
          </div>

          <h2 className={`${isDark ? "text-white" : "text-black"} text-2xl font-bold mb-6`}>
            Sign In
          </h2>

          <form onSubmit={currentFormSubmitHandler}>

            {/* IDENTIFIER */}
            <div className="mb-4 relative">
              <label className={`text-sm ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                Email
              </label>

              <input
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                disabled={isLoading || loginStep === "password"}
                placeholder="Enter your email"
                className={`
                w-full px-3 py-3 rounded-lg border-l-2 text-sm pr-10 focus:outline-none
                ${isDark
                    ? "bg-[#1E1F20] text-zinc-200 border-transparent focus:border-[#6a65ef]"
                    : "bg-white text-zinc-900 border-zinc-300 focus:border-[#6a65ef]"
                  }
              `}
              />

              {loginStep === "password" && (
                <button
                  type="button"
                  onClick={goBackToIdentifierStep}
                  className="absolute top-9 right-3 text-zinc-400 hover:text-white"
                >
                  ✎
                </button>
              )}
            </div>

            {/* PASSWORD + OTP SECTION */}
            {loginStep === "password" && (
              <>
                {/* PASSWORD */}
                {loginFlowState === "password" && (
                  <div className="mb-4">
                    <label className={`text-sm ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                      Password
                    </label>

                    <input
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className={`
                      w-full px-3 py-3 rounded-lg border-l-2 text-sm focus:outline-none
                      ${isDark
                          ? "bg-[#1E1F20] text-zinc-200 border-transparent focus:border-[#6a65ef]"
                          : "bg-white text-zinc-900 border-zinc-300 focus:border-[#6a65ef]"
                        }
                    `}
                    />
                  </div>
                )}

                {/* OTP VERIFY */}
                {loginFlowState === "otp_verify" && (
                  <div className="mb-4">
                    <label className={`text-sm ${isDark ? "text-zinc-400" : "text-zinc-600"}`}>
                      One-time code sent to {formData.emailForOtpDisplay || "your email"}
                    </label>

                    <input
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      maxLength={6}
                      placeholder="Enter your 6-Digit OTP"
                      className={`
                      w-full px-3 py-3 rounded-lg border-l-2 text-sm focus:outline-none
                      ${isDark
                          ? "bg-[#1E1F20] text-zinc-200 border-transparent focus:border-[#6a65ef]"
                          : "bg-white text-zinc-900 border-zinc-300 focus:border-[#6a65ef]"
                        }
                    `}
                    />

                    {/* RESEND OTP */}
                    <button
                      type="button"
                      onClick={handleRequestLoginOtp}
                      disabled={resendDisabled || isLoading}
                      className={`text-xs mt-2 ${resendDisabled
                        ? "text-zinc-500"
                        : isDark
                          ? "text-white underline"
                          : "text-[#6a65ef] underline"
                        }`}
                    >
                      {resendDisabled ? `Resend code in ${otpTimer}s` : "Resend code"}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* MAIN BUTTON */}
            <div className="flex flex-col items-start gap-4">

              <button
                type="submit"
                disabled={isLoading}
                className="w-auto p-[1px] rounded-3xl flex items-center bg-white gap-4 border"
              >
                <div className="text-black font-poppins px-6 font-medium h-9 flex items-center justify-center">
                  {isLoading
                    ? loginFlowState === "otp_request"
                      ? "Sending..."
                      : loginFlowState === "otp_verify"
                        ? "Verifying..."
                        : "Signing in..."
                    : mainButtonText}
                </div>

                {!isLoading && (
                  <span className="bg-[#6a65ef] text-lg h-9 w-9 rounded-full flex items-center justify-center text-white mr-[1px]">
                    <FaArrowRightLong />
                  </span>
                )}
              </button>

              {/* SWITCH PASSWORD / OTP */}
              {loginStep === "password" && (
                <div className="flex justify-center items-center gap-6">

                  {loginFlowState === "password" && (
                    <button
                      type="button"
                      onClick={switchToOtpFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with one-time code
                    </button>
                  )}

                  {loginFlowState !== "password" && (
                    <button
                      type="button"
                      onClick={switchToPasswordFlow}
                      className="text-sm text-[#6a65ef] hover:underline"
                    >
                      Sign in with password
                    </button>
                  )}

                </div>
              )}

            </div>

          </form>

          {/* OR */}
          <div className="my-6 flex items-center">
            <hr className="w-full border-zinc-600" />
            <span className="px-2 text-zinc-400 text-sm">OR</span>
            <hr className="w-full border-zinc-600" />
          </div>

          {/* OAUTH */}
          <div className="flex justify-center gap-6">
            <button onClick={handleGoogleLogin} className="w-11 h-11 bg-white rounded-full p-1">
              <img src="../assets/google.svg" className="h-full" />
            </button>

            <button onClick={handleMicrosoftLogin} className="w-11 h-11 bg-white rounded-full p-1">
              <img src="../assets/icons8-microsoft-outlook.svg" className="h-full" />
            </button>

            <button onClick={handleLinkedInLogin} className="w-11 h-11 rounded-full" style={{ background: "#0288d1" }}>
              <img src="../assets/icons8-linkedin.svg" className="h-full" />
            </button>
          </div>

          {/* TERMS */}
          <p className="mt-8 flex justify-center gap-4 text-sm text-gray-400">
            <a href="https://www.supermanager.co/terms-of-service" className="underline">Terms of Service</a>
            <a href="https://www.supermanager.co/privacy-policy" className="underline">Privacy Policy</a>
          </p>

        </div>
      </div>

      {/* RIGHT PANEL LIGHT ONLY */}
      {!isDark && (
        <div className="hidden lg:flex w-1/2 items-center justify-center p-16">
          <div className="max-w-xl">
            <h1 className="text-5xl font-semibold text-zinc-900 mb-6">
              See autonomous project management running on your real projects.
            </h1>

            <p className="text-zinc-600 text-lg mb-8">
              Experience autonomous project management in action.
            </p>

            <p className="text-zinc-500 text-sm mb-8">
              We may request additional information to verify your identity.
            </p>

            <div className="flex gap-6 text-sm text-zinc-700">
              <span className="underline cursor-pointer">Documentation</span>
              <span className="underline cursor-pointer">FAQ</span>
              <span className="underline cursor-pointer">Resource Hub</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default Login;